import React, { useState, useEffect } from "react";
import AsignacionProspecto from "./AsignacionProspecto";
import ProspectoTNPService from "../../axios_services/prospectos_tnp.service";

/**
 * Componente base para filas de prospectos TNP
 * Maneja el estilo de la fila según el estado de contactado
 */
const FilaProspectoBase = ({ prospecto, children, mostrarCheckbox, onCheckboxChange, isChecked }) => {
  const getClassName = () => {
    if (Number(prospecto.estado_id) === 12) return "estado-12";
    if (Number(prospecto.estado_id) === 11) return "estado-11";
    if (Number(prospecto.contactado_id) === 1) return "contactado-si";
    if (Number(prospecto.contactado_id) === 2) return "contactado-no";
    return "";
  };

  return (
    <tr className={getClassName()} style={{ height: '47px' }}>
      {mostrarCheckbox && (
        <AsignacionProspecto
          prospectoId={prospecto.prospecto_id}
          checked={isChecked}
          onChange={(e) => onCheckboxChange(e, prospecto)}
        />
      )}
      {children}
    </tr>
  );
};

/**
 * Componente para fila de tabla de Seguimiento TNP
 */
export const FilaSeguimientoTNP = ({
  prospecto,
  instituciones,
  estado,
  mostrarCheckbox,
  onCheckboxChange,
  isChecked,
  detalleCreditoFallido,
  user,
  isLastRows = false,
}) => {
  const [contactado, setContactado] = useState(prospecto.contactado_id ?? null);
  const [estadoId, setEstadoId] = useState(prospecto.estado_id);
  const [creditoFallidoId, setCreditoFallidoId] = useState(prospecto.credito_fallido_id || 0);
  const [isSaving, setIsSaving] = useState(false);
  const [isOpenContactado, setIsOpenContactado] = useState(false);
  const [isOpenEstado, setIsOpenEstado] = useState(false);
  const [isOpenCelulares, setIsOpenCelulares] = useState(false);

  useEffect(() => {
    setContactado(prospecto.contactado_id ?? null);
    setEstadoId(prospecto.estado_id);
    setCreditoFallidoId(prospecto.credito_fallido_id || 0);
  }, [prospecto.contactado_id, prospecto.estado_id, prospecto.credito_fallido_id]);

  const isDisabled = 
    Number(prospecto.gestor_id) != user?.usuario_id || 
    (Number(user?.perfil_id) !== 3 && (Number(user?.perfil_id) !== 4 && Number(user?.perfil_id) !== 27 && Number(user?.perfil_id) !== 28));

  const handleGestion = async (newContactado, newEstado, newCreditoFallido) => {
    try {
      setIsSaving(true);
      await ProspectoTNPService.gestionarProspectoTNP(
        prospecto.prospecto_id,
        newContactado,
        newEstado,
        user?.usuario_id,
        newCreditoFallido
      );
      prospecto.contactado_id = newContactado;
      prospecto.estado_id = newEstado;
      prospecto.credito_fallido_id = newCreditoFallido;
    } catch (error) {
      alert("Error al actualizar prospecto TNP.");
      setContactado(prospecto.contactado_id ?? null);
      setEstadoId(prospecto.estado_id);
    } finally {
      setIsSaving(false);
    }
  };

  const onContactadoChange = (val) => {
    setContactado(val);
    setEstadoId(null);
    setCreditoFallidoId(0);
    setIsOpenContactado(false);
    handleGestion(val, null, 0);
  };

  const onEstadoChange = (val) => {
    const newDetalle = Number(val) !== 12 ? 0 : creditoFallidoId;
    setEstadoId(val);
    setCreditoFallidoId(newDetalle);
    setIsOpenEstado(false);
    handleGestion(contactado, val, newDetalle);
  };

  const [isOpenDetalle, setIsOpenDetalle] = useState(false);
  const onDetalleChange = (val) => {
    setCreditoFallidoId(val);
    setIsOpenDetalle(false);
    handleGestion(contactado, estadoId, val);
  };

  const getConvenio = () => {
    const institucion = instituciones.find(
      (item) => item.institucion_id === prospecto.razon_social_id
    );
    return institucion?.razon_social || "N/A";
  };

  const renderCelulares = () => {
    if (!prospecto.celulares || prospecto.celulares.length === 0) {
      return "SIN REGISTRO";
    }

    return (
      <div className="text-[10px] text-gray-800 whitespace-normal min-w-[150px] max-w-[250px] select-text" style={{ userSelect: 'text', WebkitUserSelect: 'text' }}>
        {prospecto.celulares.map((celular, index) => (
          <React.Fragment key={index}>
            {celular}
            {index < prospecto.celulares.length - 1 && ", "}
          </React.Fragment>
        ))}
      </div>
    );
  };

  const renderContactadoDropdown = () => {
    const getLabel = () => {
      if (Number(contactado) === 1) return "CONTACTADO";
      if (Number(contactado) === 2) return "INTENTADO";
      return "PENDIENTE";
    };

    return (
      <div className="relative flex items-center justify-between min-w-[90px]">
        <span className="text-[10px] text-gray-700">{getLabel()}</span>
        <button
          onClick={() => !isDisabled && !isSaving && Number(estadoId) !== 11 && setIsOpenContactado(!isOpenContactado)}
          className="text-gray-500 hover:text-blue-600 focus:outline-none p-0.5 rounded transition-colors disabled:opacity-50 ml-1"
          type="button"
          disabled={isDisabled || isSaving || Number(estadoId) === 11}
        >
          <svg className="w-3 h-3" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m19 9-7 7-7-7"/>
          </svg>
        </button>

        {isOpenContactado && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setIsOpenContactado(false)}></div>
            <div className={`absolute z-50 ${isLastRows ? 'bottom-full mb-1' : 'top-full mt-1'} bg-white border border-gray-200 rounded shadow-lg w-28 left-0 text-left`}>
              <ul className="py-1 text-[10px] text-gray-700">
                <li>
                  <button onClick={() => onContactadoChange(1)} className="block w-full px-2 py-1.5 hover:bg-gray-100 text-left uppercase">CONTACTADO</button>
                </li>
                <li>
                  <button onClick={() => onContactadoChange(2)} className="block w-full px-2 py-1.5 hover:bg-gray-100 text-left uppercase">INTENTADO</button>
                </li>
              </ul>
            </div>
          </>
        )}
      </div>
    );
  };
  const renderEstadoDropdown = () => {
    const isReadyForEstado = Number(contactado) === 1 || Number(contactado) === 2;
    const opcionesEstado = isReadyForEstado ? estado.filter((item) =>
      Number(contactado) === 1
        ? (item.tipo_id >= 1 && item.tipo_id <= 5) || (item.tipo_id >= 9 && item.tipo_id <= 10) || (item.tipo_id == 12)
        : (item.tipo_id >= 6 && item.tipo_id <= 9)
    ) : [];

    const getLabel = () => {
      const selected = estado.find(item => Number(item.tipo_id) === Number(estadoId));
      return selected ? selected.descripcion.toUpperCase() : "PENDIENTE";
    };

    return (
      <div className="relative flex items-center justify-between min-w-[100px]">
        <span className="text-[10px] text-gray-700 truncate max-w-[100px]">{getLabel()}</span>
        <button
          onClick={() => !isDisabled && !isSaving && isReadyForEstado && Number(estadoId) !== 11 && setIsOpenEstado(!isOpenEstado)}
          className="text-gray-500 hover:text-blue-600 focus:outline-none p-0.5 rounded transition-colors disabled:opacity-50 ml-1"
          type="button"
          disabled={isDisabled || isSaving || !isReadyForEstado || Number(estadoId) === 11}
        >
          <svg className="w-3 h-3 flex-shrink-0" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m19 9-7 7-7-7"/>
          </svg>
        </button>

        {isOpenEstado && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setIsOpenEstado(false)}></div>
            <div className={`absolute z-50 ${isLastRows ? 'bottom-full mb-1' : 'top-full mt-1'} bg-white border border-gray-200 rounded shadow-lg w-auto min-w-[160px] max-w-[260px] left-0 text-left`}>
              <ul className="py-1 text-[10px] text-gray-700">
                {opcionesEstado.filter(i => [1, 3, 12].includes(Number(i.tipo_id))).length > 0 && (
                  <>
                    <li className="px-2 py-1 text-[8px] font-bold text-gray-500 bg-gray-50 uppercase">Proceso</li>
                    {opcionesEstado.filter(i => [1, 3, 12].includes(Number(i.tipo_id))).map((item) => (
                      <li key={item.tipo_id}>
                        <button
                          onClick={() => onEstadoChange(item.tipo_id)}
                          className="block w-full px-4 py-1.5 hover:bg-gray-100 text-left uppercase"
                        >
                          {item.descripcion}
                        </button>
                      </li>
                    ))}
                  </>
                )}
                {opcionesEstado.filter(i => [2, 4, 5, 6, 7, 8, 10, 9].includes(Number(i.tipo_id))).length > 0 && (
                   <>
                     <li className="px-2 py-1 text-[8px] font-bold text-gray-500 bg-gray-50 uppercase">Motivo</li>
                     {opcionesEstado.filter(i => [2, 4, 5, 6, 7, 8, 10, 9].includes(Number(i.tipo_id)))
                      .sort((a, b) => Number(a.tipo_id) === 9 ? 1 : Number(b.tipo_id) === 9 ? -1 : Number(a.tipo_id) - Number(b.tipo_id))
                      .map((item) => (
                      <li key={item.tipo_id}>
                        <button
                          onClick={() => onEstadoChange(item.tipo_id)}
                          className="block w-full px-4 py-1.5 hover:bg-gray-100 text-left uppercase"
                        >
                          {item.descripcion}
                        </button>
                      </li>
                    ))}
                  </>
                )}
              </ul>
            </div>
          </>
        )}
      </div>
    );
  };

  const renderDetalleDropdown = () => {
    const isReadyForDetalle = Number(estadoId) === 12;
    const getLabel = () => {
      const selected = detalleCreditoFallido.find(item => Number(item.tipo_id) === Number(creditoFallidoId));
      return selected ? selected.descripcion.toUpperCase() : "NINGUNA";
    };

    return (
      <div className="relative flex items-center justify-between min-w-[100px]">
        <span className="text-[10px] text-gray-700 truncate max-w-[100px]">{getLabel()}</span>
        <button
          onClick={() => !isDisabled && !isSaving && isReadyForDetalle && setIsOpenDetalle(!isOpenDetalle)}
          className="text-gray-500 hover:text-blue-600 focus:outline-none p-0.5 rounded transition-colors disabled:opacity-50 ml-1"
          type="button"
          disabled={isDisabled || isSaving || !isReadyForDetalle}
        >
          <svg className="w-3 h-3 flex-shrink-0" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m19 9-7 7-7-7"/>
          </svg>
        </button>

        {isOpenDetalle && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setIsOpenDetalle(false)}></div>
            <div className={`absolute z-50 ${isLastRows ? 'bottom-full mb-1' : 'top-full mt-1'} bg-white border border-gray-200 rounded shadow-lg w-auto min-w-[160px] max-w-[260px] left-0 text-left`}>
              <ul className="py-1 text-[10px] text-gray-700">
                <li className="px-2 py-1 text-[8px] font-bold text-gray-500 bg-gray-50 uppercase">Cmac</li>
                {detalleCreditoFallido.filter(i => Number(i.tipo_id) >= 1 && Number(i.tipo_id) <= 4).map((item) => (
                  <li key={item.tipo_id}>
                    <button
                      onClick={() => onDetalleChange(item.tipo_id)}
                      className="block w-full px-4 py-1.5 hover:bg-gray-100 text-left uppercase"
                    >
                      {item.descripcion}
                    </button>
                  </li>
                ))}
                <li className="px-2 py-1 text-[8px] font-bold text-gray-500 bg-gray-50 uppercase">Prospecto</li>
                {detalleCreditoFallido.filter(i => Number(i.tipo_id) >= 5 && Number(i.tipo_id) <= 6).map((item) => (
                  <li key={item.tipo_id}>
                    <button
                      onClick={() => onDetalleChange(item.tipo_id)}
                      className="block w-full px-4 py-1.5 hover:bg-gray-100 text-left uppercase"
                    >
                      {item.descripcion}
                    </button>
                  </li>
                ))}
                <li className="px-2 py-1 text-[8px] font-bold text-gray-500 bg-gray-50 uppercase">Mec</li>
                {detalleCreditoFallido.filter(i => Number(i.tipo_id) === 7).map((item) => (
                  <li key={item.tipo_id}>
                    <button
                      onClick={() => onDetalleChange(item.tipo_id)}
                      className="block w-full px-4 py-1.5 hover:bg-gray-100 text-left uppercase"
                    >
                      {item.descripcion}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <FilaProspectoBase
      prospecto={prospecto}
      mostrarCheckbox={mostrarCheckbox}
      onCheckboxChange={onCheckboxChange}
      isChecked={isChecked}
    >
      <td className="select-text" style={{ userSelect: 'text', WebkitUserSelect: 'text' }}>{prospecto.dni}</td>
      <td>{prospecto.nombre}</td>
      <td>{renderCelulares()}</td>
      <td>{getConvenio()}</td>
      <td>{renderContactadoDropdown()}</td>
      <td>{renderEstadoDropdown()}</td>
      <td>{renderDetalleDropdown()}</td>
      {(Number(user?.perfil_id) === 6 || Number(user?.perfil_id) === 8) && (
        <td>{prospecto.gestor_nombre ? prospecto.gestor_nombre.toUpperCase() : 'SIN COLABORADOR'}</td>
      )}
    </FilaProspectoBase>
  );
};

/**
 * Componente para fila de tabla de Datos Personales TNP
 */
export const FilaDatosPersonalesTNP = ({
  prospecto,
  estadoCivil,
  contratoCondicion,
  formatoSoles,
  mostrarCheckbox,
  onCheckboxChange,
  isChecked,
}) => {
  const getEstadoCivil = () => {
    const ec = estadoCivil.find((ec) => ec.tipo_id === prospecto.estado_civil_id);
    return ec?.descripcion || "N/A";
  };

  const getContratoCondicion = () => {
    const cc = contratoCondicion.find((cc) => cc.tipo_id === prospecto.contrato_condicion);
    return cc?.descripcion || "N/A";
  };

  return (
    <FilaProspectoBase
      prospecto={prospecto}
      mostrarCheckbox={mostrarCheckbox}
      onCheckboxChange={onCheckboxChange}
      isChecked={isChecked}
    >
      <td className="select-text" style={{ userSelect: 'text', WebkitUserSelect: 'text' }}>{prospecto.dni}</td>
      <td>{prospecto.nombre}</td>
      <td>{prospecto.email || "N/A"}</td>
      <td>{prospecto.edad || "N/A"}</td>
      <td>{getEstadoCivil()}</td>
      <td>{prospecto.direccion || "N/A"}</td>
      <td>{prospecto.consentimiento_datos || "N/A"}</td>
      <td>{prospecto.dni_conyuge || "N/A"}</td>
      <td>{prospecto.cargo || "N/A"}</td>
      <td>{getContratoCondicion()}</td>
      <td>{prospecto.rango_ingresos ? formatoSoles(prospecto.rango_ingresos) : "N/A"}</td>
    </FilaProspectoBase>
  );
};

export const FilaVaciaTNP = ({ colSpan, mensaje = "No hay prospectos TNP disponibles" }) => {
  return (
    <tr>
      <td colSpan={colSpan} className="text-center">
        {mensaje}
      </td>
    </tr>
  );
};
